import {
  websiteDiscoverySchema,
  type WebsiteDiscoveryConfig,
} from '@kanbu/schema/contracts';
import {
  Role,
  SourceType,
  UpdateFrequency,
  type RemovalBehavior,
} from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import * as Tabs from '@radix-ui/react-tabs';
import { Button, Dialog, Input, Label, Switch, toast } from '@utima/ui';
import { memo, useEffect, useMemo, useState } from 'react';

import { LockedTabLabel } from '@/components';
import { WebsiteDiscoveryOptions } from '@/features/documents/components/addDocumentsModal/WebsiteDiscoveryOptions';
import { AgentAccessSelector } from '@/features/documents/components/AgentAccessSelector';
import { AutoUpdateSettingsForm } from '@/features/documents/components/AutoUpdateSettingsForm';
import { useAccess } from '@/hooks/useAccess';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { trpc } from '@/services/trpc';

import { FolderDefaultPriorityHint } from './FolderDefaultPriorityHint';
import { PrioritySelect } from './PrioritySelect';
import type { useKBMutations } from '../hooks/useKBMutations';
import { isManagedSource, isWebsiteDiscoveryRoot } from '../lib/managedSource';

export type FolderSettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderId: string;
  defaultTab?: 'general' | 'auto-update' | 'agents';
  mutations: ReturnType<typeof useKBMutations>;
};

export const FolderSettingsModal = memo(function FolderSettingsModal({
  open,
  onOpenChange,
  folderId,
  defaultTab = 'general',
  mutations,
}: FolderSettingsModalProps) {
  const { t } = useLingui();
  const { hasAccess } = useAccess();
  const isSuperAdmin = hasAccess([Role.SuperAdmin]);
  const { canUseKbAutoUpdates, isKbAutoUpdatesLocked } =
    useSubscriptionFeatures();
  const [activeTab, setActiveTab] = useState<
    'general' | 'auto-update' | 'agents'
  >('general');

  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab, open]);

  useEffect(() => {
    if (!open) {
      setActiveTab('general');
    }
  }, [open]);

  const { data: folder } = trpc.documentFolders.findOne.useQuery(
    { id: folderId },
    { enabled: open },
  );

  const { data: allChats } = trpc.chats.findAll.useQuery(
    {
      where: { organization: folder?.organization?.id },
      pagination: { pageSize: 100 },
      fields: ['id', 'name'],
    },
    { enabled: open && !!folder?.organization?.id },
  );

  // General tab state
  const [discovery, setDiscovery] = useState<WebsiteDiscoveryConfig>(
    websiteDiscoverySchema.parse({ method: 'quick' }),
  );
  const [includeSubfolders, setIncludeSubfolders] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultWeight, setDefaultWeight] = useState('1');

  // Auto-update tab state
  const [updateFrequency, setUpdateFrequency] = useState<
    UpdateFrequency | null | undefined
  >(null);
  const [discoveryFrequency, setDiscoveryFrequency] = useState<
    UpdateFrequency | null | undefined
  >(null);
  const [changeThreshold, setChangeThreshold] = useState<
    number | null | undefined
  >(null);
  const [autoIncludeNewPages, setAutoIncludeNewPages] = useState<
    boolean | null | undefined
  >(null);
  const [removalBehavior, setRemovalBehavior] = useState<
    RemovalBehavior | null | undefined
  >(null);

  // Agent access tab state
  const [availableForAllAgents, setAvailableForAllAgents] = useState(false);
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);

  const isManaged = isManagedSource(folder);
  const isDrive = folder?.sourceType === SourceType.GoogleDrive;
  const isFeedSource = isDrive || folder?.sourceType === SourceType.Rss;
  const isManagedChild = isFeedSource && !folder?.sourceUri;
  const isWebsiteRoot = isWebsiteDiscoveryRoot(folder);
  const isSubpathFolder = !!folder?.websitePathPrefix;
  const isDiscoveryOn =
    discoveryFrequency != null && discoveryFrequency !== UpdateFrequency.Never;

  const inheritedChatIds = useMemo(() => {
    if (!folder) return [];

    const directIds = new Set(
      folder.chats?.map((chat: string | { id: string }) =>
        typeof chat === 'object' ? chat.id : chat,
      ) ?? [],
    );

    const effectiveIds =
      (folder as { effectiveChatIds?: string[] }).effectiveChatIds ?? [];

    return effectiveIds.filter(id => !directIds.has(id));
  }, [folder]);

  // Sync state when folder data loads
  useEffect(() => {
    if (!folder) return;

    setIncludeSubfolders(
      folder.sourceConfig?.sourceType === SourceType.GoogleDrive &&
        folder.sourceConfig.includeSubfolders,
    );
    setDiscovery(
      websiteDiscoverySchema.parse(
        folder.sourceConfig?.sourceType === SourceType.Website
          ? (folder.sourceConfig.discovery ?? { method: 'quick' })
          : { method: 'quick' },
      ),
    );
    setName(folder.name);
    setDescription(folder.description ?? '');
    setDefaultWeight(String(folder.defaultWeight ?? 1));
    setUpdateFrequency(folder.updateFrequency ?? null);
    setDiscoveryFrequency(folder.discoveryFrequency ?? null);
    setChangeThreshold(folder.changeThreshold ?? null);
    setAutoIncludeNewPages(folder.autoIncludeNewPages ?? null);
    setRemovalBehavior(folder.removalBehavior ?? null);
    setAvailableForAllAgents(folder.availableForAllAgents ?? false);
    setSelectedChatIds(folder.chats?.map((c: { id: string }) => c.id) ?? []);
  }, [folder]);

  const handleSave = async () => {
    try {
      // Update general settings without availableForAllAgents to avoid
      // double propagation (assignToChats handles it atomically).
      await mutations.updateFolder.mutateAsync({
        id: folderId,
        ...(isWebsiteRoot && { discovery }),
        ...(isDrive && folder?.sourceUri && { includeSubfolders }),
        name: name.trim(),
        description: description.trim() || null,
        defaultWeight: Number.parseFloat(defaultWeight),
        ...(canUseKbAutoUpdates && {
          ...(!isManagedChild && { updateFrequency: updateFrequency ?? null }),
          ...(!isFeedSource && {
            discoveryFrequency: isWebsiteRoot
              ? (discoveryFrequency ?? null)
              : null,
            changeThreshold: isSuperAdmin
              ? (changeThreshold ?? null)
              : (folder?.changeThreshold ?? null),
          }),
          ...(!isFeedSource && {
            autoIncludeNewPages: isManaged ? autoIncludeNewPages : null,
          }),
          removalBehavior: isManaged ? (removalBehavior ?? null) : null,
        }),
      });

      // Sync agent assignments + availableForAllAgents in one call
      await mutations.assignToChats.mutateAsync({
        folderId,
        chatIds: availableForAllAgents ? [] : selectedChatIds,
        availableForAllAgents,
      });

      toast.success(t`Folder updated`);
      onOpenChange(false);
    } catch {
      toast.error(t`Failed to update folder`);
    }
  };

  const tabTriggerClass =
    'px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='w-[600px]'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Folder Settings</Trans>
          </Dialog.Title>
        </Dialog.Header>

        <Tabs.Root
          value={activeTab}
          onValueChange={value => {
            if (
              value === 'general' ||
              value === 'auto-update' ||
              value === 'agents'
            ) {
              setActiveTab(value);
            }
          }}
          className='flex min-h-0 flex-col'
        >
          <Tabs.List className='border-b border-border'>
            <div className='flex gap-2'>
              <Tabs.Trigger value='general' className={tabTriggerClass}>
                <Trans>General</Trans>
              </Tabs.Trigger>
              <Tabs.Trigger value='agents' className={tabTriggerClass}>
                <Trans>Agent Access</Trans>
              </Tabs.Trigger>
              <Tabs.Trigger value='auto-update' className={tabTriggerClass}>
                <LockedTabLabel locked={isKbAutoUpdatesLocked}>
                  <Trans>Auto-Update</Trans>
                </LockedTabLabel>
              </Tabs.Trigger>
            </div>
          </Tabs.List>

          {/* General tab */}
          <Tabs.Content value='general' className='space-y-4 py-4'>
            {isWebsiteRoot && (
              <WebsiteDiscoveryOptions
                value={discovery}
                onChange={value => {
                  setDiscovery(value);
                  if (value.method === 'single') setDiscoveryFrequency(null);
                }}
                mirror
                mirrorLocked
                onMirrorChange={() => {}}
                discoveryEnabled={isDiscoveryOn}
              />
            )}
            <div className='space-y-1.5'>
              <Label htmlFor='folderName'>
                <Trans>Name</Trans>
              </Label>
              <Input
                id='folderName'
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={isManaged}
              />
              {isManaged && (
                <p className='text-xs text-muted-fg'>
                  <Trans>
                    Name is auto-managed for connected source folders and cannot
                    be changed.
                  </Trans>
                </p>
              )}
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='folderDescription'>
                <Trans>Description</Trans>
              </Label>
              <Input
                id='folderDescription'
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='defaultWeight'>
                <Trans>Default priority</Trans>
              </Label>
              <PrioritySelect
                id='defaultWeight'
                value={defaultWeight}
                onValueChange={setDefaultWeight}
              />
              <FolderDefaultPriorityHint />
            </div>
          </Tabs.Content>

          {/* Agent Access tab */}
          <Tabs.Content value='agents' className='space-y-4 py-4'>
            <p className='text-sm text-muted-fg'>
              <Trans>
                Changes will apply to all documents in this folder and
                subfolders.
              </Trans>
            </p>
            {allChats?.items && (
              <AgentAccessSelector
                availableForAllAgents={availableForAllAgents}
                setAvailableForAllAgents={setAvailableForAllAgents}
                selectedChatIds={selectedChatIds}
                setSelectedChatIds={setSelectedChatIds}
                availableAgents={allChats.items}
                inheritedChatIds={inheritedChatIds}
              />
            )}
          </Tabs.Content>

          <Tabs.Content value='auto-update' className='space-y-4 py-4'>
            {isDrive && folder?.sourceUri && (
              <div className='space-y-2'>
                <div className='flex items-center justify-between gap-2'>
                  <Label htmlFor='folderIncludeSubfolders'>
                    <Trans>Include subfolders</Trans>
                  </Label>
                  <Switch
                    id='folderIncludeSubfolders'
                    checked={includeSubfolders}
                    onCheckedChange={setIncludeSubfolders}
                  />
                </div>
                <p className='text-xs text-muted-fg'>
                  <Trans>
                    Applies on the next manual or scheduled sync. Turning this
                    off treats previously synced nested files as removed and
                    applies their removal policy.
                  </Trans>
                </p>
              </div>
            )}
            {isFeedSource && (
              <p className='text-sm text-muted-fg'>
                <Trans>
                  Each sync checks for changed and removed files and
                  automatically imports new items. The source root controls the
                  sync schedule for the whole hierarchy.
                </Trans>
              </p>
            )}
            <fieldset
              disabled={isKbAutoUpdatesLocked}
              className='space-y-4 disabled:pointer-events-none disabled:opacity-50'
            >
              <AutoUpdateSettingsForm
                mode='single'
                showUpdateFrequency={!isManagedChild}
                showChangeThreshold={isSuperAdmin && !isFeedSource}
                showDiscoveryFrequency={
                  isWebsiteRoot && discovery.method !== 'single'
                }
                showWebsiteFields={isManaged}
                showAutoIncludeNewPages={
                  !isFeedSource &&
                  isManaged &&
                  (!isWebsiteRoot || isDiscoveryOn)
                }
                showAutoIncludeRootScanHint={isSubpathFolder}
                updateFrequency={updateFrequency}
                discoveryFrequency={discoveryFrequency}
                changeThreshold={changeThreshold}
                autoIncludeNewPages={autoIncludeNewPages}
                removalBehavior={removalBehavior}
                onUpdateFrequencyChange={setUpdateFrequency}
                onDiscoveryFrequencyChange={setDiscoveryFrequency}
                onChangeThresholdChange={setChangeThreshold}
                onAutoIncludeNewPagesChange={value =>
                  setAutoIncludeNewPages(!!value)
                }
                onRemovalBehaviorChange={setRemovalBehavior}
              />
            </fieldset>
          </Tabs.Content>
        </Tabs.Root>

        <Dialog.Footer>
          <Button variant='ghost' outline onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={handleSave}
            loading={
              mutations.updateFolder.isPending ||
              mutations.assignToChats.isPending
            }
            disabled={!name.trim()}
          >
            <Trans>Save</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
